import { useEffect, useState } from "react";
import { api, fmtEUR, MONTHLY_LIMIT, customerSearchMode } from "@/lib/api";
import { useSortableTable, SortIndicator } from "@/lib/use-sortable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
    Plus,
    PencilSimple,
    Trash,
    Users,
    MagnifyingGlass,
    IdentificationCard,
    ArrowUUpLeft,
    CheckCircle,
    XCircle,
} from "@phosphor-icons/react";
import CustomerCardDialog from "@/components/CustomerCardDialog";
import CustomerLookupBar from "@/components/CustomerLookupBar";
import CustomerDetailPanel from "@/components/CustomerDetailPanel";
import { useAuth } from "@/lib/auth-context";

const GREEK_MONTHS = [
    "ΙΑΝΟΥΑΡΙΟ", "ΦΕΒΡΟΥΑΡΙΟ", "ΜΑΡΤΙΟ", "ΑΠΡΙΛΙΟ", "ΜΑΪΟ", "ΙΟΥΝΙΟ",
    "ΙΟΥΛΙΟ", "ΑΥΓΟΥΣΤΟ", "ΣΕΠΤΕΜΒΡΙΟ", "ΟΚΤΩΒΡΙΟ", "ΝΟΕΜΒΡΙΟ", "ΔΕΚΕΜΒΡΙΟ",
];
const currentMonthLabel = () => {
    const d = new Date();
    return `${GREEK_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const empty = {
    code: "",
    afm: "",
    name: "",
    position: "",
    phone: "",
    email: "",
    qr_barcode: "",
    info: "",
    monthly_limit: "25",
    no_credit_control: false,
};

export default function Customers() {
    const { canEdit } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [cardCustomer, setCardCustomer] = useState(null);
    const [lookupValue, setLookupValue] = useState("");
    const [lookupSearching, setLookupSearching] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [creditFilter, setCreditFilter] = useState("ALL"); // ALL | WITH | WITHOUT

    const performLookup = async (rawQ) => {
        const q = (rawQ || "").trim();
        if (!q) {
            toast.error("Εισάγετε κωδικό, ΑΦΜ ή barcode");
            return;
        }
        setLookupSearching(true);
        try {
            const mode = customerSearchMode(q);
            const url = `/customers/search/${encodeURIComponent(q)}${
                mode ? `?mode=${mode}` : ""
            }`;
            const { data } = await api.get(url);
            setSelectedCustomer(data);
            setLookupValue("");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Δεν βρέθηκε πελάτης");
        } finally {
            setLookupSearching(false);
        }
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/customers");
            setCustomers(data);
        } catch {
            toast.error("Αποτυχία φόρτωσης πελατών");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openNew = async () => {
        setEditing(null);
        setForm({ ...empty, code: "..." });
        setDialogOpen(true);
        try {
            const { data } = await api.get("/customers/next-code");
            setForm((f) => ({ ...f, code: data.code }));
        } catch {
            setForm((f) => ({ ...f, code: "" }));
            toast.error("Αποτυχία ανάκτησης επόμενου κωδικού");
        }
    };

    const openEdit = (c) => {
        setEditing(c);
        setForm({
            code: c.code,
            afm: c.afm || "",
            name: c.name,
            position: c.position || "",
            phone: c.phone || "",
            email: c.email || "",
            qr_barcode: c.qr_barcode || "",
            info: c.info || "",
            monthly_limit: String(c.period_limit ?? c.monthly_limit ?? 25),
            no_credit_control: !!c.no_credit_control,
        });
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e?.preventDefault?.();
        if (!form.afm.trim()) {
            toast.error("Το ΑΦΜ είναι υποχρεωτικό");
            return;
        }
        if (!form.name.trim()) {
            toast.error("Το όνομα είναι υποχρεωτικό");
            return;
        }
        const limit = parseFloat(String(form.monthly_limit).replace(",", "."));
        if (!limit || limit <= 0) {
            toast.error("Η πίστωση μήνα πρέπει να είναι > 0");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                afm: form.afm.trim(),
                name: form.name.trim(),
                position: form.position.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                qr_barcode: form.qr_barcode.trim(),
                info: form.info.trim(),
                monthly_limit: limit,
                no_credit_control: !!form.no_credit_control,
            };
            if (editing) {
                await api.put(`/customers/${editing.id}`, payload);
                toast.success("Ο πελάτης ενημερώθηκε");
            } else {
                await api.post("/customers", payload);
                toast.success("Ο πελάτης προστέθηκε");
            }
            setDialogOpen(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Σφάλμα αποθήκευσης");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await api.delete(`/customers/${confirmDelete.id}`);
            toast.success("Ο πελάτης διαγράφηκε");
            setConfirmDelete(null);
            await load();
        } catch {
            toast.error("Αποτυχία διαγραφής");
        }
    };

    const filtered = customers.filter((c) => {
        if (creditFilter === "WITH" && (c.remaining ?? 0) <= 0) return false;
        if (creditFilter === "WITHOUT" && (c.remaining ?? 0) > 0) return false;
        return true;
    });

    const customersSort = useSortableTable(filtered, "code", "asc", {
        code: (c) => c.code,
        name: (c) => c.name || "",
        remaining: (c) => Number(c.remaining ?? 0),
        period_limit: (c) => Number(c.period_limit ?? c.monthly_limit ?? 0),
        afm: (c) => c.afm || "",
        phone: (c) => c.phone || "",
    });
    const sortedCustomers = customersSort.sorted;

    const counts = {
        ALL: customers.length,
        WITH: customers.filter((c) => (c.remaining ?? 0) > 0).length,
        WITHOUT: customers.filter((c) => (c.remaining ?? 0) <= 0).length,
    };

    const goToTop = () => {
        setSelectedCustomer(null);
        setLookupValue("");
        setCreditFilter("ALL");
        const el = document.querySelector('[data-testid="customers-page"]');
        if (el) el.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <div className="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 max-w-7xl w-full" data-testid="customers-page">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4 fade-up">
                <div>
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5F66] mb-2">
                        ΔΙΑΧΕΙΡΙΣΗ
                    </div>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">
                        ΠΕΛΑΤΕΣ
                    </h1>
                    <p className="mt-2 text-sm text-[#5C5F66]">
                        Σύνολο: {customers.length} πελάτες
                    </p>
                </div>
                <div className="flex gap-2 items-end flex-wrap">
                    {(selectedCustomer ||
                        creditFilter !== "ALL" ||
                        lookupValue.trim()) && (
                        <Button
                            onClick={goToTop}
                            data-testid="back-to-top-button"
                            variant="outline"
                            className="rounded-none h-12 px-5 border-2 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white font-bold tracking-wide"
                        >
                            <ArrowUUpLeft size={18} weight="bold" className="mr-2" />
                            ΕΠΙΣΤΡΟΦΗ
                        </Button>
                    )}
                    <Button
                        onClick={openNew}
                        data-testid="add-customer-button"
                        className={`rounded-none h-12 px-6 bg-[#002FA7] hover:bg-[#001D66] text-white font-bold tracking-wide transition-all duration-200 active:scale-[0.98] ${
                            canEdit ? "" : "hidden"
                        }`}
                    >
                        <Plus size={18} weight="bold" className="mr-2" />
                        ΝΕΟΣ ΠΕΛΑΤΗΣ
                    </Button>
                </div>
            </div>

            {/* Quick lookup with scanner & numpad */}
            <div className="mb-6 fade-up">
                <CustomerLookupBar
                    value={lookupValue}
                    onChange={setLookupValue}
                    onSearch={performLookup}
                    searching={lookupSearching}
                />
            </div>

            {/* Detail panel — visible when a customer is found */}
            {selectedCustomer && (
                <CustomerDetailPanel
                    customer={selectedCustomer}
                    onClose={() => setSelectedCustomer(null)}
                />
            )}

            {/* Filter */}
            {!selectedCustomer && (
                <div className="mb-6 fade-up">
                    {/* Credit-status tabs */}
                    <div className="flex gap-2 flex-wrap" data-testid="credit-filter-tabs">
                        <FilterTab
                            active={creditFilter === "ALL"}
                            onClick={() => setCreditFilter("ALL")}
                            testId="credit-filter-all"
                            icon={Users}
                            label="ΟΛΟΙ"
                            count={counts.ALL}
                            color="#0A0A0A"
                        />
                        <FilterTab
                            active={creditFilter === "WITH"}
                            onClick={() => setCreditFilter("WITH")}
                            testId="credit-filter-with"
                            icon={CheckCircle}
                            label="ΜΕ ΕΝΕΡΓΟ CREDIT"
                            count={counts.WITH}
                            color="#1F8A3E"
                        />
                        <FilterTab
                            active={creditFilter === "WITHOUT"}
                            onClick={() => setCreditFilter("WITHOUT")}
                            testId="credit-filter-without"
                            icon={XCircle}
                            label="ΧΩΡΙΣ CREDIT"
                            count={counts.WITHOUT}
                            color="#B11919"
                        />
                    </div>
                </div>
            )}

            {/* Table — hidden when a customer is selected */}
            {!selectedCustomer && (
                <div className="border border-[#E2E4E9] bg-white fade-up">
                {loading ? (
                    <div className="p-16 text-center text-sm font-bold uppercase tracking-[0.2em] text-[#5C5F66]">
                        Φορτωση...
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-16 text-center">
                        <Users
                            size={48}
                            weight="thin"
                            className="mx-auto text-[#5C5F66] mb-4"
                        />
                        <div className="text-sm text-[#5C5F66]">
                            {customers.length === 0
                                ? "Δεν υπάρχουν πελάτες ακόμη. Πατήστε «Νέος Πελάτης»."
                                : "Δεν βρέθηκαν πελάτες με αυτά τα κριτήρια."}
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-b border-[#0A0A0A] hover:bg-transparent">
                                    <SortableHead
                                        sort={customersSort}
                                        sortKey="code"
                                        label="Κωδικος"
                                    />
                                    <SortableHead
                                        sort={customersSort}
                                        sortKey="name"
                                        label="Ονομα"
                                    />
                                    <SortableHead
                                        sort={customersSort}
                                        sortKey="remaining"
                                        label="Υπολοιπο Μηνα"
                                        align="right"
                                    />
                                    <SortableHead
                                        sort={customersSort}
                                        sortKey="period_limit"
                                        label="Πιστωση"
                                        align="right"
                                    />
                                    <SortableHead
                                        sort={customersSort}
                                        sortKey="afm"
                                        label="ΑΦΜ"
                                    />
                                    <SortableHead
                                        sort={customersSort}
                                        sortKey="phone"
                                        label="Τηλεφωνο"
                                    />
                                    <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-12 text-right w-32">
                                        Ενεργειες
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedCustomers.map((c) => {
                                    const limitSet = c.period_limit_set !== false;
                                    const limit = limitSet
                                        ? (c.period_limit ?? c.monthly_limit ?? MONTHLY_LIMIT)
                                        : 0;
                                    const maxed = limitSet && c.remaining <= 0;
                                    const warn =
                                        limitSet && c.remaining <= 5 && !maxed;
                                    return (
                                        <TableRow
                                            key={c.id}
                                            className="border-b border-[#E2E4E9] hover:bg-[#F4F5F7]"
                                            data-testid={`customer-row-${c.code}`}
                                        >
                                            <TableCell>
                                                <Badge className="rounded-none bg-[#0A0A0A] text-white font-mono text-xs">
                                                    #{c.code}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="font-semibold text-[#0A0A0A]">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span>{c.name}</span>
                                                    {c.is_demo && (
                                                        <span
                                                            className="inline-block px-1.5 py-0.5 text-[9px] font-black tracking-wider text-white bg-[#A855F7]"
                                                            data-testid={`demo-badge-${c.code}`}
                                                            title="Δοκιμαστικός πελάτης — οι κινήσεις του δεν εμφανίζονται στα reports"
                                                        >
                                                            🧪 DEMO
                                                        </span>
                                                    )}
                                                    {c.no_credit_control && !c.is_demo && (
                                                        <span
                                                            className="inline-block px-1.5 py-0.5 text-[9px] font-black tracking-wider text-white bg-[#FF9500]"
                                                            data-testid={`vip-badge-${c.code}`}
                                                            title="ΧΩΡΙΣ ΕΛΕΓΧΟ ΠΙΣΤΩΣΗΣ — Πλαφόν 10.000€"
                                                        >
                                                            🛡️ VIP
                                                        </span>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <span
                                                    className={`inline-block px-3 py-1.5 border-2 font-bold font-mono text-sm ${
                                                        c.is_demo
                                                            ? "border-[#A855F7] text-[#7E22CE] bg-[#F5E9FF]"
                                                            : !limitSet
                                                            ? "border-[#5C5F66] text-[#5C5F66] bg-[#F4F5F7]"
                                                            : maxed
                                                              ? "border-[#FF3B30] text-[#B11919] bg-[#FDEAE8]"
                                                              : warn
                                                                ? "border-[#FFCC00] text-[#7A5C00] bg-[#FFF8DD]"
                                                                : "border-[#34C759] text-[#1F8A3E] bg-[#EAF7EE]"
                                                    }`}
                                                    data-testid={`customer-remaining-${c.code}`}
                                                >
                                                    {c.is_demo ? fmtEUR(9999) : limitSet ? fmtEUR(c.remaining) : "—"}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-xs text-[#5C5F66]">
                                                {c.is_demo ? fmtEUR(9999) : limitSet ? fmtEUR(limit) : "ΧΩΡΙΣ ΟΡΙΟ"}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-[#1A1A1A]">
                                                {c.afm || "—"}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs text-[#1A1A1A]">
                                                {c.phone || "—"}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button
                                                        onClick={() => setCardCustomer(c)}
                                                        data-testid={`card-customer-${c.code}`}
                                                        className="p-2 hover:bg-[#002FA7] hover:text-white text-[#002FA7] transition-colors"
                                                        title="Καρτέλα πελάτη"
                                                    >
                                                        <IdentificationCard size={16} weight="bold" />
                                                    </button>
                                                    {canEdit && !c.is_demo && (
                                                        <>
                                                            <button
                                                                onClick={() =>
                                                                    openEdit(c)
                                                                }
                                                                data-testid={`edit-customer-${c.code}`}
                                                                className="p-2 hover:bg-[#0A0A0A] hover:text-white text-[#0A0A0A] transition-colors"
                                                                title="Επεξεργασία"
                                                            >
                                                                <PencilSimple
                                                                    size={16}
                                                                    weight="bold"
                                                                />
                                                            </button>
                                                            <button
                                                                onClick={() =>
                                                                    setConfirmDelete(
                                                                        c
                                                                    )
                                                                }
                                                                data-testid={`delete-customer-${c.code}`}
                                                                className="p-2 hover:bg-[#FF3B30] hover:text-white text-[#FF3B30] transition-colors"
                                                                title="Διαγραφή"
                                                            >
                                                                <Trash
                                                                    size={16}
                                                                    weight="bold"
                                                                />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent
                    className="rounded-none border-2 border-[#0A0A0A] max-w-3xl p-0 gap-0 max-h-[92vh] overflow-y-auto"
                    data-testid="customer-form-dialog"
                >
                    <DialogHeader className="p-7 border-b border-[#E2E4E9]">
                        <DialogTitle className="font-heading text-3xl font-black tracking-tighter text-[#0A0A0A]">
                            {editing ? "ΕΠΕΞΕΡΓΑΣΙΑ ΠΕΛΑΤΗ" : "ΝΕΟΣ ΠΕΛΑΤΗΣ"}
                        </DialogTitle>
                        <DialogDescription className="text-base text-[#5C5F66]">
                            {editing
                                ? "Ο κωδικός πελάτη δεν αλλάζει."
                                : "Ο κωδικός θα δοθεί αυτόματα από το σύστημα."}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSave}>
                        <div className="p-7 space-y-5">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Κωδικος
                                    </Label>
                                    <Input
                                        value={form.code}
                                        readOnly
                                        disabled
                                        data-testid="form-code-input"
                                        className="rounded-none h-14 text-2xl font-bold border-[#E2E4E9] bg-[#F4F5F7] text-[#0A0A0A] font-mono cursor-not-allowed"
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        ΑΦΜ *
                                    </Label>
                                    <Input
                                        value={form.afm}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                afm: e.target.value,
                                            })
                                        }
                                        placeholder="123456789"
                                        data-testid="form-afm-input"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] font-mono"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                    Ονομα *
                                </Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            name: e.target.value,
                                        })
                                    }
                                    placeholder="Ονοματεπώνυμο"
                                    data-testid="form-name-input"
                                    className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7]"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                    Θεση πελατη
                                </Label>
                                <Input
                                    value={form.position}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            position: e.target.value,
                                        })
                                    }
                                    placeholder="Π.χ. Διεύθυνση, ταμείο, τμήμα..."
                                    data-testid="form-position-input"
                                    className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7]"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Τηλεφωνο
                                    </Label>
                                    <Input
                                        value={form.phone}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                phone: e.target.value,
                                            })
                                        }
                                        placeholder="69xxxxxxxx"
                                        data-testid="form-phone-input"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] font-mono"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        Email
                                    </Label>
                                    <Input
                                        type="email"
                                        value={form.email}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                email: e.target.value,
                                            })
                                        }
                                        placeholder="email@example.com"
                                        data-testid="form-email-input"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7]"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        {editing
                                            ? `Πιστωση για τον τρεχοντα μηνα (€) *`
                                            : `Πιστωση μηνα (€) *`}
                                    </Label>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        value={form.monthly_limit}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                monthly_limit: e.target.value,
                                            })
                                        }
                                        placeholder="π.χ. 25 ή 80"
                                        data-testid="form-monthly-limit-input"
                                        className="rounded-none h-14 text-xl font-bold border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] font-mono"
                                    />
                                    {editing && (
                                        <p className="text-[11px] text-[#5C5F66] leading-snug">
                                            ⓘ Η αλλαγή ισχύει <b>μόνο για τον τρέχοντα μήνα</b> ({currentMonthLabel()}).
                                            Τον επόμενο μήνα ο πελάτης θα επανέλθει στο γενικό όριο.
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                        QR / Barcode
                                    </Label>
                                    <Input
                                        value={form.qr_barcode}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                qr_barcode: e.target.value,
                                            })
                                        }
                                        placeholder="Προαιρετικός κωδικός σάρωσης"
                                        data-testid="form-qr-input"
                                        className="rounded-none h-14 text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7] font-mono"
                                    />
                                </div>
                            </div>
                            {/* VIP / No credit control switch */}
                            <div className="border-2 border-[#FF9500] bg-[#FFF8E1] p-4 flex items-start gap-4">
                                <div className="flex-1">
                                    <Label className="text-sm font-black uppercase tracking-[0.15em] text-[#0A0A0A] flex items-center gap-2">
                                        🛡️ ΧΩΡΙΣ ΕΛΕΓΧΟ ΠΙΣΤΩΣΗΣ (VIP)
                                    </Label>
                                    <p className="mt-1 text-xs text-[#5C5F66] leading-relaxed">
                                        Όταν ενεργοποιηθεί, ο πελάτης έχει
                                        σταθερό μηνιαίο πλαφόν{" "}
                                        <strong>5.000€</strong> και δεν
                                        επηρεάζεται από το global μηνιαίο όριο.
                                        Μηδενίζει την 1η κάθε μήνα.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={!!form.no_credit_control}
                                    data-testid="form-no-credit-control"
                                    onClick={() =>
                                        setForm({
                                            ...form,
                                            no_credit_control: !form.no_credit_control,
                                        })
                                    }
                                    className={`shrink-0 w-14 h-8 relative transition-colors ${
                                        form.no_credit_control
                                            ? "bg-[#FF9500]"
                                            : "bg-[#E2E4E9]"
                                    }`}
                                >
                                    <span
                                        className={`absolute top-1 left-1 w-6 h-6 bg-white transition-transform ${
                                            form.no_credit_control
                                                ? "translate-x-6"
                                                : ""
                                        }`}
                                    />
                                </button>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm font-bold uppercase tracking-[0.15em]">
                                    Πληροφοριες
                                </Label>
                                <Textarea
                                    value={form.info}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            info: e.target.value,
                                        })
                                    }
                                    placeholder="Προαιρετικές σημειώσεις για τον πελάτη..."
                                    rows={3}
                                    data-testid="form-info-input"
                                    className="rounded-none text-base border-[#E2E4E9] focus-visible:border-[#002FA7] focus-visible:ring-1 focus-visible:ring-[#002FA7]"
                                />
                            </div>
                        </div>
                        <DialogFooter className="p-7 border-t border-[#E2E4E9] gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setDialogOpen(false)}
                                className="rounded-none h-14 px-8 border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#F4F5F7] font-bold text-base"
                            >
                                ΑΚΥΡΟ
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                data-testid="form-save-button"
                                className="rounded-none h-14 px-8 bg-[#002FA7] hover:bg-[#001D66] text-white font-bold text-base transition-all duration-200 active:scale-[0.98]"
                            >
                                {saving
                                    ? "ΑΠΟΘΗΚΕΥΣΗ..."
                                    : editing
                                      ? "ΕΝΗΜΕΡΩΣΗ"
                                      : "ΔΗΜΙΟΥΡΓΙΑ"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog
                open={!!confirmDelete}
                onOpenChange={(o) => !o && setConfirmDelete(null)}
            >
                <AlertDialogContent
                    className="rounded-none border-2 border-[#0A0A0A] max-w-lg"
                    data-testid="delete-confirm-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle className="font-heading font-black tracking-tighter text-2xl">
                            ΔΙΑΓΡΑΦΗ ΠΕΛΑΤΗ
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-base">
                            Πρόκειται να διαγράψετε τον πελάτη{" "}
                            <strong>{confirmDelete?.name}</strong> (#
                            {confirmDelete?.code}) και όλο το ιστορικό
                            χρεώσεών του. Η ενέργεια δεν αναιρείται.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-3">
                        <AlertDialogCancel className="rounded-none h-12 px-6 border-[#0A0A0A] font-bold">
                            ΑΚΥΡΟ
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            data-testid="confirm-delete-button"
                            className="rounded-none h-12 px-6 bg-[#FF3B30] hover:bg-[#cc2f26] text-white font-bold"
                        >
                            ΔΙΑΓΡΑΦΗ
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Customer card */}
            <CustomerCardDialog
                open={!!cardCustomer}
                customer={cardCustomer}
                onClose={() => setCardCustomer(null)}
            />
        </div>
    );
}

function FilterTab({ active, onClick, icon: Icon, label, count, color, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`flex items-center gap-2 px-4 py-2.5 border-2 transition-all active:scale-[0.97] ${
                active
                    ? "text-white shadow"
                    : "bg-white text-[#0A0A0A] hover:bg-[#FAFAFA]"
            }`}
            style={{
                backgroundColor: active ? color : undefined,
                borderColor: color,
            }}
        >
            <Icon size={16} weight={active ? "fill" : "bold"} />
            <span className="text-xs font-black uppercase tracking-wider">
                {label}
            </span>
            <span
                className={`text-[11px] font-mono font-bold px-1.5 py-0.5 ${
                    active
                        ? "bg-white/20 text-white"
                        : "bg-[#FAFAFA] text-[#5C5F66]"
                }`}
            >
                {count}
            </span>
        </button>
    );
}

function SortableHead({ sort, sortKey, label, align = "left" }) {
    const active = sort.sortKey === sortKey;
    return (
        <TableHead
            onClick={() => sort.toggle(sortKey)}
            data-testid={`sort-customers-${sortKey}`}
            className={`text-[10px] font-bold uppercase tracking-[0.15em] text-[#0A0A0A] h-12 cursor-pointer select-none hover:bg-[#F4F5F7] ${
                align === "right" ? "text-right" : ""
            } ${active ? "bg-[#F4F5F7]" : ""}`}
        >
            {label}
            <SortIndicator active={active} direction={sort.sortDir} />
        </TableHead>
    );
}

